/**
 * Session id management.
 *
 * Stored in `sessionStorage` under `kz_sid` when consent is granted. Lifetime is
 * tied to the browser tab. Ephemeral random per page load when consent is not
 * granted — never written to storage.
 */

import { random64BitHex } from './random';

const STORAGE_KEY = 'kz_sid';

function safeSessionStorage(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

export function readPersistentSessionId(): string | null {
  const store = safeSessionStorage();
  if (!store) return null;
  try {
    return store.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export type SessionLifecycle = 'init' | 'refresh';

/**
 * Ensures a tab session id exists. Returns whether this call reused an existing id
 * (`refresh`) or created one (`init`).
 */
export function ensurePersistentSessionIdWithLifecycle(): {
  sessionId: string;
  sessionLifecycle: SessionLifecycle;
} {
  const existing = readPersistentSessionId();
  if (existing) {
    return { sessionId: existing, sessionLifecycle: 'refresh' };
  }
  const id = random64BitHex();
  const store = safeSessionStorage();
  if (store) {
    try {
      store.setItem(STORAGE_KEY, id);
    } catch {
      /* quota / private mode — id stays in-memory only */
    }
  }
  return { sessionId: id, sessionLifecycle: 'init' };
}

export function ensurePersistentSessionId(): string {
  return ensurePersistentSessionIdWithLifecycle().sessionId;
}

/**
 * Persists an id minted outside the consent-checked identity path — the
 * dying-page emergency flush reports `session_init` under it. Without this the
 * tab's next page would find storage empty and open a second session for the
 * same tab (yoto writes its sid at script time, so it counts one).
 */
export function writePersistentSessionId(sessionId: string): void {
  const store = safeSessionStorage();
  if (!store) return;
  try {
    store.setItem(STORAGE_KEY, sessionId);
  } catch {
    /* quota / private mode — id stays in-memory only */
  }
}

export function clearPersistentSessionId(): void {
  const store = safeSessionStorage();
  if (!store) return;
  try {
    store.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function generateEphemeralSessionId(): string {
  return random64BitHex();
}
